/**
 * O documento de cinema como ele existe no armazém.
 *
 * Espelha `MediaItem.toMap`/`fromMap` em
 * `lib/features/cinema/data/models/media_item.dart`. Os dois apps leem e
 * escrevem a mesma coleção, então um campo renomeado aqui é um campo perdido
 * para o outro lado.
 */

import { idioma, traduzir } from '../i18n/idioma';
import type { Casa } from './casa';

export const COLECAO_CINEMA = 'watch_list';

/**
 * O status é aberto de propósito.
 *
 * O Flutter grava `watched`, mas também `watched-<slug>`, `watched-self` e
 * `watched-both` — e o mesmo para `watching`. Uma união fechada de três
 * strings fazia o web ler como "para ver" tudo que a casa tinha marcado por
 * pessoa. Os três literais ficam para o autocompletar; o `(string & {})` é o
 * que segura o resto sem virar `string` puro.
 */
export type StatusMidia = 'to-watch' | 'watching' | 'watched' | (string & {});

export interface DocMidia {
  tmdbId: number;
  title: string;
  mediaType: 'movie' | 'tv';
  /** URL inteira, não o caminho cru do TMDB: é assim que o Flutter grava. */
  posterPath: string;
  backdropPath: string;
  year: string;
  status: StatusMidia;
  isAnime: boolean;
  /** Quem adicionou. */
  userName: string;
  addedAt: string;
  /** `MM-DD`, para o "neste dia" não ter que reabrir a data toda. */
  monthDay: string;
  source: string;
  synopsis: string;
  airingStatus: string;
  format: string;
  studio: string;
  anilistId?: number;
  episodeCount?: number;
  genres?: string[];
  score?: number;
  currentSeason?: number;
  currentEpisode?: number;
  currentTimestamp?: number;
  progressUpdatedAt?: string;
}

/** `MediaItem.isWatched`: qualquer `watched-<slug>` também conta. */
export const estaVisto = (status: string): boolean =>
  status === 'watched' || status.startsWith('watched-');

/** `MediaItem.isCurrentlyWatching`. */
export const estaAssistindo = (status: string): boolean =>
  status === 'watching' || status.startsWith('watching-');

/** `MediaItem.isToWatch`. Só o literal, como lá. */
export const paraVer = (status: string): boolean => status === 'to-watch';

/** `MediaItem.isMovie`: tudo que não é série é filme, inclusive anime. */
export const ehFilme = (tipo: string): boolean => tipo !== 'tv';

const BASE_POSTER = 'https://image.tmdb.org/t/p/w500';
const BASE_FUNDO = 'https://image.tmdb.org/t/p/w1280';

/** `MediaItem.posterUrl`: o que já é absoluto passa inteiro. */
export function posterDoDoc(caminho: string): string {
  if (!caminho) return '';
  return caminho.startsWith('http') ? caminho : `${BASE_POSTER}${caminho}`;
}

/** `MediaItem.backdropUrl` — w1280, e não o w500 do pôster. */
export function fundoDoDoc(caminho: string): string {
  if (!caminho) return '';
  return caminho.startsWith('http') ? caminho : `${BASE_FUNDO}${caminho}`;
}

/** `MediaItem.partnerUsernames`: o campo é uma lista disfarçada de string. */
export function quemQuer(userName: string): string[] {
  return userName
    .split(',')
    .map((u) => u.trim())
    .filter(Boolean);
}

const slugsDaCasa = (casa?: Casa | null): string[] => (casa?.pessoas ?? []).map((p) => p.slug);

const nomeDe = (casa: Casa | null | undefined, slug: string): string =>
  casa?.pessoas.find((p) => p.slug === slug)?.nome ?? slug;

/**
 * `MediaItem.wanterDisplay`. O nome sai da casa, nunca do código.
 *
 * A emenda final é à mão, como em `juntosEmPalavras()`: o `Intl.ListFormat`
 * faria os dois idiomas sozinho, mas o Hermes do celular não o traz.
 */
export function rotuloDeQuemQuer(userName: string, casa?: Casa | null): string {
  const slugs = slugsDaCasa(casa);
  const gente = quemQuer(userName).filter((u) => slugs.includes(u));
  if (gente.length === 0) return traduzir('Meu');
  if (slugs.length > 0 && slugs.every((s) => gente.includes(s))) return traduzir('Todo mundo');
  return gente.map((s) => nomeDe(casa, s)).join(idioma() === 'pt' ? ' e ' : ' and ');
}

/**
 * `MediaItem.watchedDisplay`.
 *
 * O `status` que entra é a palavra gravada — `watched-self`, `watching-` mais
 * o slug —, e continua crua: só o que sai vira fala. O nome da pessoa vem da
 * casa e por isso entra como buraco, nunca como verbete.
 */
export function rotuloDeStatus(status: string, casa?: Casa | null): string {
  if (status === 'watched' || status === 'watched-both') return traduzir('Todo mundo viu');
  if (status === 'watched-self') return traduzir('Visto');
  if (status.startsWith('watched-')) {
    const slug = status.slice('watched-'.length);
    const pessoa = casa?.pessoas.find((p) => p.slug === slug);
    return pessoa ? traduzir('{quem} viu', { quem: pessoa.nome }) : traduzir('Visto');
  }
  if (status === 'watching' || status === 'watching-both' || status === 'watching-self') {
    return traduzir('Assistindo');
  }
  if (status.startsWith('watching-')) {
    const slug = status.slice('watching-'.length);
    const pessoa = casa?.pessoas.find((p) => p.slug === slug);
    return pessoa ? traduzir('{quem} assistindo', { quem: pessoa.nome }) : traduzir('Assistindo');
  }
  return traduzir('Para ver');
}

/**
 * `MediaItem.resolveCoupleStatus`: o `-self` gravado por um aparelho vira o
 * `-<slug>` ou `-both` que os botões da gaveta destacam.
 */
export function statusDaCasa(status: string, userName: string, casa?: Casa | null): string {
  if (status === 'watched-both' || status === 'watching-both') return status;
  if (status !== 'watched-self' && status !== 'watching-self') return status;

  const prefixo = status.startsWith('watched') ? 'watched' : 'watching';
  const slugs = slugsDaCasa(casa);
  const gente = quemQuer(userName).filter((u) => slugs.includes(u));
  if (gente.length === 0) return `${prefixo}-self`;
  if (slugs.length > 0 && slugs.every((s) => gente.includes(s))) return `${prefixo}-both`;
  if (gente.length === 1) return `${prefixo}-${gente[0]}`;
  // Mais de uma pessoa e não a casa inteira: vale a ordem da casa, para o
  // rótulo não trocar de nome entre duas leituras.
  return `${prefixo}-${slugs.find((s) => gente.includes(s))}`;
}
